import { Injectable } from '@angular/core';
import {
  DefaultDataService,
  DefaultDataServiceConfig,
  EntityCollectionServiceBase,
  EntityCollectionServiceElementsFactory,
  HttpUrlGenerator
} from '@ngrx/data';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Conversation, Message, Participant } from '@twilio/conversations';

import { ResponseModel } from '@modules/auth/_models/response.model';
import { getConversationUnreadCount } from '@modules/messenger';

import { ConversationStateModel, MessageStateModel, ParticipantStateModel, SettingsStateModel } from './store-models';
import { StateEntity } from './entity.constants';
import { UserDataModel } from '@modules/auth';

export class EntityMap<T> {
  private readonly dataMap = new Map<string, T>();

  public get(id: string): T {
    return this.dataMap.get(id);
  }

  public set(id: string, data: T): void {
    this.dataMap.set(id, data);
  }

  public remove(id: string): void {
    this.dataMap.delete(id);
  }

  public clear(): void {
    this.dataMap.clear();
  }
}

export class EntityCollectionServiceWithActiveRecord<EntityModel, ActiveRecordType> extends EntityCollectionServiceBase<EntityModel> {
  public readonly entityMap: EntityMap<ActiveRecordType>;
}

@Injectable({providedIn: 'root'})
export class UsersDataService extends DefaultDataService<UserDataModel> {
  constructor(
    httpUrlGenerator: HttpUrlGenerator,
    httpClient: HttpClient,
    config?: DefaultDataServiceConfig,
  ) {
    super(StateEntity.User, httpClient, httpUrlGenerator, config);
  }

  getAll() {
    return super.getAll().pipe(
      map((response) => this.parseResponse(response as unknown as ResponseModel<UserDataModel[]>)),
    );
  }

  getWithQuery(params) {
    return super.getWithQuery(params).pipe(
      map(response => this.parseResponse(response as unknown as ResponseModel<UserDataModel[]>)),
    );
  }

  parseResponse(response: ResponseModel<UserDataModel[]>) {
    return response?.data;
  }
}


@Injectable({providedIn: 'root'})
export class UsersCollectionService extends EntityCollectionServiceBase<UserDataModel> {
  constructor(serviceElementsFactory: EntityCollectionServiceElementsFactory) {
    super(StateEntity.User, serviceElementsFactory);
  }
}

@Injectable({providedIn: 'root'})
export class SettingsCollectionService extends EntityCollectionServiceBase<SettingsStateModel> {
  constructor(serviceElementsFactory: EntityCollectionServiceElementsFactory) {
    super(StateEntity.Setting, serviceElementsFactory);
  }
}


@Injectable({providedIn: 'root'})
export class ConversationsCollectionService extends EntityCollectionServiceWithActiveRecord<ConversationStateModel, Conversation> {
  entityMap = new EntityMap<Conversation>();

  totalUnreadMessagesCount$: Observable<number> = this.entities$.pipe(
    map(conversations => conversations.reduce((acc, conversation) =>
      acc + getConversationUnreadCount(conversation), 0)),
  );

  constructor(serviceElementsFactory: EntityCollectionServiceElementsFactory) {
    super(StateEntity.Conversation, serviceElementsFactory);
  }
}

@Injectable({providedIn: 'root'})
export class ParticipantsCollectionService extends EntityCollectionServiceWithActiveRecord<ParticipantStateModel, Participant> {
  entityMap = new EntityMap<Participant>();

  constructor(serviceElementsFactory: EntityCollectionServiceElementsFactory) {
    super(StateEntity.Participant, serviceElementsFactory);
  }
}

@Injectable({providedIn: 'root'})
export class MessagesCollectionService extends EntityCollectionServiceWithActiveRecord<MessageStateModel, Message> {
  entityMap = new EntityMap<Message>();

  constructor(serviceElementsFactory: EntityCollectionServiceElementsFactory) {
    super(StateEntity.Message, serviceElementsFactory);
  }
}
